import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { ActiveSiteBanner } from '../components/ActiveSiteBanner';
import { Avatar } from '../components/Avatar';
import { useConfirm } from '../components/ConfirmProvider';
import {
  getSite,
  listActiveSignIns,
  listTodaySignIns,
  signOutPerson,
  type Site,
  type SignIn,
} from '../lib/db';
import { captureGeolocation, distanceMeters } from '../lib/geo';
import { generateSignInLogPdf } from '../lib/pdf';
import { useUserOrg } from '../lib/org';
import { LoadingIndicator } from '../components/LoadingIndicator';

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function durationSince(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
}

function locationChip(p: SignIn, s: Site | null): { text: string; cls: string } | null {
  if (p.in_lat == null || p.in_lng == null) return null;
  if (s && s.centre_lat != null && s.centre_lng != null) {
    const dist = Math.round(distanceMeters(p.in_lat, p.in_lng, s.centre_lat, s.centre_lng));
    const radius = s.geofence_radius_m ?? 200;
    if (dist <= radius) {
      const acc = p.in_accuracy_m ? ` ±${Math.round(p.in_accuracy_m)}m` : '';
      return { text: `📍 On site${acc}`, cls: 'badge-success' };
    }
    return { text: `⚠ ${dist}m off-site`, cls: 'badge-warn' };
  }
  return { text: '📍 Logged', cls: 'badge-neutral' };
}

export function SiteRegister() {
  const { siteId } = useParams<{ siteId: string }>();
  const nav = useNavigate();
  const { org } = useUserOrg();
  const { confirm, alert } = useConfirm();
  const [site, setSite] = useState<Site | null>(null);
  const [active, setActive] = useState<SignIn[]>([]);
  const [today, setToday] = useState<SignIn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState<string | null>(null);

  async function refresh() {
    if (!siteId) return;
    setLoading(true);
    try {
      const [s, a, t] = await Promise.all([
        getSite(siteId),
        listActiveSignIns(siteId),
        listTodaySignIns(siteId),
      ]);
      setSite(s);
      setActive(a);
      setToday(t.filter((r) => r.time_out !== null));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [siteId]);

  async function onSignOut(person: SignIn) {
    if (!(await confirm({
      title: `Sign ${person.full_name} out?`,
      body: 'Their time-out will be stamped now and the entry locked.',
      confirmLabel: 'Sign out',
    }))) return;
    setSigningOut(person.id);
    setError(null);
    try {
      const geo = await captureGeolocation(5000);
      await signOutPerson(person.id, {
        lat: geo?.lat ?? null,
        lng: geo?.lng ?? null,
        address: geo?.address ?? null,
        accuracy_m: geo?.accuracy_m ?? null,
      });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSigningOut(null);
    }
  }

  const [exporting, setExporting] = useState(false);

  async function exportToday() {
    if (!site || !org) return;
    const all = [...active, ...today].sort(
      (a, b) => new Date(a.time_in).getTime() - new Date(b.time_in).getTime()
    );
    if (all.length === 0) {
      await alert({
        title: 'Nothing to export',
        body: 'No sign-ins today yet.',
      });
      return;
    }
    setExporting(true);
    try {
      const blob = await generateSignInLogPdf({
        orgName: org.org_name,
        orgLogoPath: org.logo_path,
        site,
        signIns: all,
      });
      const url = URL.createObjectURL(blob);
      const dateStr = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sign-in-log-${site.name.replace(/\s+/g, '-')}-${dateStr}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  }

  if (loading && !site) {
    return <AppShell title="Sign In / Out" back="/"><LoadingIndicator /></AppShell>;
  }

  if (!site) {
    return (
      <AppShell title="Sign In / Out" back="/">
        <div className="banner banner-error">Site not found.</div>
      </AppShell>
    );
  }

  return (
    <AppShell
      title="Sign In / Out"
      documentTitle={`Sign in · ${site.name}`}
      back="/"
    >
      <ActiveSiteBanner site={site} />

      {error && <div className="banner banner-error">{error}</div>}

      <button
        type="button"
        className="btn btn-primary signin-cta"
        onClick={() => nav(`/sites/${site.id}/sign-in`)}
      >
        Sign someone in →
      </button>
      <button
        type="button"
        className="btn btn-ghost"
        style={{ marginTop: 8 }}
        onClick={() => nav(`/sites/${site.id}/signin-qr`)}
      >
        Sign-in QR / public link →
      </button>
      <button
        type="button"
        className="btn btn-ghost"
        style={{ marginTop: 8 }}
        onClick={() => nav(`/sites/${site.id}/timesheet`)}
      >
        Hours worked / timesheet →
      </button>
      <button
        type="button"
        className="btn btn-ghost"
        style={{ marginTop: 8 }}
        onClick={exportToday}
        disabled={exporting}
      >
        {exporting ? 'Exporting today’s sign-ins…' : 'Export today’s sign-ins as PDF'}
      </button>

      <h2 className="section-title" style={{ marginTop: 26 }}>
        Currently on site <span className="count-badge">{active.length}</span>
      </h2>

      {active.length > 0 && (
        <>
          <p className="muted" style={{ margin: '0 0 12px' }}>
            Tap <strong>Sign out</strong> on a person to mark them out — or close the site to sign everyone out at once.
          </p>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ marginBottom: 12 }}
            onClick={() => nav(`/sites/${site.id}/close`)}
          >
            Close site for today →
          </button>
        </>
      )}

      {active.length === 0 ? (
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
            No-one is signed in right now.
          </p>
        </div>
      ) : (
        <ul className="signin-list">
          {active.map((p) => {
            const chip = locationChip(p, site);
            return (
              <li key={p.id} className="signin-card">
                <Avatar name={p.full_name} />
                <div className="signin-card-body">
                  <p className="signin-card-name">{p.full_name}</p>
                  <p className="signin-card-meta">
                    {[p.company, p.trade_role].filter(Boolean).join(' · ')}
                  </p>
                  <p className="signin-card-time">In since {fmtTime(p.time_in)} · {durationSince(p.time_in)}</p>
                  {chip && (
                    <p style={{ margin: '4px 0 0' }}>
                      <span className={`badge ${chip.cls}`}>{chip.text}</span>
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  className="btn-outline"
                  disabled={signingOut === p.id}
                  onClick={() => onSignOut(p)}
                >
                  {signingOut === p.id ? '…' : 'Sign out'}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <h2 className="section-title" style={{ marginTop: 28 }}>Today's history</h2>
      {today.length === 0 ? (
        <div className="card" style={{ textAlign: 'center' }}>
          {active.length > 0 ? (
            <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
              Everyone signed in today is still on site. Names will appear here when they sign out.
            </p>
          ) : (
            <p style={{ margin: 0, color: 'var(--ink-soft)' }}>No sign-ins today yet.</p>
          )}
        </div>
      ) : (
        <ul className="signin-list">
          {today.map((p) => (
            <li key={p.id} className="signin-card signin-card-done">
              <Avatar name={p.full_name} />
              <div className="signin-card-body">
                <p className="signin-card-name">{p.full_name}</p>
                <p className="signin-card-meta">{p.company}</p>
                <p className="signin-card-time">
                  {fmtTime(p.time_in)} → {p.time_out ? fmtTime(p.time_out) : '—'}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </AppShell>
  );
}
